import { useState, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  useTimelineData,
  type TimelineEvent,
  type TimelineEra,
  type TimelinePerson,
  type TimelineScene,
} from "@/hooks/useTimelineData";
import { TimelineCanvas, type TimelineCanvasHandle } from "@/components/timeline/TimelineCanvas";
import { HoverCard } from "@/components/timeline/HoverCard";
import { EditModal } from "@/components/timeline/EditModal";
import { PeopleSidebar } from "@/components/timeline/PeopleSidebar";
import { TimelineSearch } from "@/components/timeline/TimelineSearch";
import { SceneDetailPopup } from "@/components/timeline/SceneDetailPopup";
import { ContentDetailPopup } from "@/components/timeline/ContentDetailPopup";
import { ContextMenu } from "@/components/timeline/ContextMenu";

type HoveredItem =
  | { type: "event"; item: TimelineEvent; x: number; y: number }
  | { type: "era"; item: TimelineEra; x: number; y: number }
  | { type: "person"; item: TimelinePerson; x: number; y: number }
  | { type: "scene"; item: TimelineScene; x: number; y: number }
  | null;

type EditingItem =
  | { type: "event"; item: TimelineEvent }
  | { type: "era"; item: TimelineEra }
  | null;

type ContentPopup =
  | { type: "event"; item: TimelineEvent; x: number; y: number }
  | { type: "era"; item: TimelineEra; x: number; y: number }
  | { type: "person"; item: TimelinePerson; x: number; y: number }
  | null;

type Props = {
  onBack: () => void;
};

function parseDate(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.getFullYear() + d.getMonth() / 12 + d.getDate() / 365;
}

export function Timeline({ onBack }: Props) {
  const { user } = useAuth();
  const { events, eras, people, scenes, isLoading, error, refetch, updateEvent, updateEra, updatePerson } =
    useTimelineData();

  const canvasRef = useRef<TimelineCanvasHandle>(null);
  const [visiblePeople, setVisiblePeople] = useState<string[]>([]);
  const [hoveredItem, setHoveredItem] = useState<HoveredItem>(null);
  const [editingItem, setEditingItem] = useState<EditingItem>(null);

  // Scene popup state
  const [scenePopup, setScenePopup] = useState<{
    sceneId: string;
    x: number;
    y: number;
  } | null>(null);

  // Content popup state (for events/eras/people)
  const [contentPopup, setContentPopup] = useState<ContentPopup>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    date: string;
    x: number;
    y: number;
  } | null>(null);

  const canEdit = user?.role === "admin" || user?.role === "editor";

  const canEditScope = (scope: string) => {
    if (!user || user.isPublic) return false;
    if (user.role === "admin") return true;
    if (user.role !== "editor") return false;
    if (!user.editPermissions) return true; // null = all scopes
    return user.editPermissions.includes(scope);
  };

  // Handle saving content from popup
  const handleContentSave = useCallback(async (type: string, id: string, data: Record<string, unknown>) => {
    if (type === "event") {
      await updateEvent(id, data as Partial<TimelineEvent>);
    } else if (type === "era") {
      await updateEra(id, data as Partial<TimelineEra>);
    } else if (type === "person") {
      await updatePerson(id, data as Partial<TimelinePerson>);
    }
    refetch();
  }, [updateEvent, updateEra, updatePerson, refetch]);

  // Handle search result selection - navigate to the selected item
  const handleSearchSelect = useCallback(
    (result: { type: "event" | "era" | "person" | "scene"; year?: number | null; startYear?: number; endYear?: number; birthYear?: number; deathYear?: number | null; item: TimelineEvent | TimelineEra | TimelinePerson | TimelineScene }) => {
      // Handle scene selection - open scene popup
      if (result.type === "scene") {
        const sceneItem = result.item as TimelineScene;
        setScenePopup({
          sceneId: sceneItem.id,
          x: window.innerWidth / 2,
          y: window.innerHeight - 400,
        });

        // If scene has a date, zoom to it
        if (result.year && canvasRef.current) {
          const padding = 10;
          canvasRef.current.navigateTo(result.year - padding, result.year + padding, 400);
        }
        return;
      }

      if (!canvasRef.current) return;

      let targetYear: number;
      let padding = 15;

      if (result.type === "event" && "year" in result) {
        targetYear = result.year as number;
        padding = 10;
      } else if (result.type === "era" && "startYear" in result && "endYear" in result) {
        const startYear = result.startYear as number;
        const endYear = result.endYear as number;
        targetYear = (startYear + endYear) / 2;
        padding = Math.max((endYear - startYear) / 2 + 5, 15);
      } else if (result.type === "person" && "birthYear" in result) {
        const birthYear = result.birthYear as number;
        const deathYear = (result.deathYear as number | null) ?? 2024;
        targetYear = (birthYear + deathYear) / 2;
        padding = Math.max((deathYear - birthYear) / 2 + 5, 20);

        setVisiblePeople((prev) => {
          const filtered = prev.filter((id) => id !== result.item.id);
          return [...filtered, result.item.id];
        });
      } else {
        return;
      }

      const targetStart = targetYear - padding;
      const targetEnd = targetYear + padding;

      canvasRef.current.navigateTo(targetStart, targetEnd, 600);

      // Open content popup for the selected item
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;

      if (result.type === "event") {
        setContentPopup({ type: "event", item: result.item as TimelineEvent, x: centerX, y: centerY });
      } else if (result.type === "era") {
        setContentPopup({ type: "era", item: result.item as TimelineEra, x: centerX, y: centerY });
      } else {
        setContentPopup({ type: "person", item: result.item as TimelinePerson, x: centerX, y: centerY });
      }
    },
    []
  );

  const handleTogglePerson = useCallback((id: string) => {
    setVisiblePeople((prev) => {
      if (prev.includes(id)) {
        return prev.filter((pid) => pid !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const handleSelectAllPeople = useCallback(() => {
    setVisiblePeople(people.map((p) => p.id));
  }, [people]);

  const handleSelectNoPeople = useCallback(() => {
    setVisiblePeople([]);
  }, []);

  const handleHover = useCallback((item: HoveredItem) => {
    setHoveredItem(item);
  }, []);

  // Handle scene click - open popup and zoom to scene
  const handleSceneClick = useCallback((sceneId: string, x: number, y: number) => {
    setScenePopup({ sceneId, x, y });

    // Find the scene and zoom to it
    const scene = scenes.find(s => s.id === sceneId);
    if (scene?.computedDate && canvasRef.current) {
      const year = parseDate(scene.computedDate);
      if (year !== null) {
        const padding = 10;
        canvasRef.current.navigateTo(year - padding, year + padding, 400);
      }
    }
  }, [scenes]);

  // Handle click on events/eras - open content popup
  const handleClick = useCallback(
    (item: HoveredItem) => {
      if (!item) return;

      if (item.type === "event") {
        setContentPopup({ type: "event", item: item.item as TimelineEvent, x: item.x, y: item.y });
      } else if (item.type === "era") {
        setContentPopup({ type: "era", item: item.item as TimelineEra, x: item.x, y: item.y });
      } else if (item.type === "person") {
        setContentPopup({ type: "person", item: item.item as TimelinePerson, x: item.x, y: item.y });
      }
    },
    []
  );

  // Handle right-click context menu
  const handleContextMenu = useCallback((date: string, x: number, y: number) => {
    if (!canEdit) return;
    setContextMenu({ date, x, y });
  }, [canEdit]);

  // Create a new scene at the given date
  const handleCreateScene = useCallback(async () => {
    if (!contextMenu) return;

    try {
      const res = await fetch("/api/scenes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          sceneCode: "L1",
          title: "New Scene",
          status: "draft",
          timelineDate: contextMenu.date,
        }),
      });

      if (!res.ok) throw new Error("Failed to create scene");

      const data = await res.json();
      await refetch();

      // Open the scene popup for the new scene
      setScenePopup({
        sceneId: data.scene.id,
        x: contextMenu.x,
        y: contextMenu.y,
      });
    } catch (e) {
      console.error("Failed to create scene:", e);
    }

    setContextMenu(null);
  }, [contextMenu, refetch]);

  // Create a new event at the given date
  const handleCreateEvent = useCallback(async () => {
    if (!contextMenu) return;

    try {
      const res = await fetch("/api/timeline/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: "New Event",
          date: contextMenu.date,
          description: "",
        }),
      });

      if (!res.ok) throw new Error("Failed to create event");

      const data = await res.json();
      await refetch();

      // Open the content popup for the new event
      setContentPopup({
        type: "event",
        item: data.event,
        x: contextMenu.x,
        y: contextMenu.y,
      });
    } catch (e) {
      console.error("Failed to create event:", e);
    }

    setContextMenu(null);
  }, [contextMenu, refetch]);

  const handleSave = useCallback(
    async (data: Record<string, unknown>) => {
      if (!editingItem) return;

      if (editingItem.type === "event") {
        await updateEvent(editingItem.item.id, data as Partial<TimelineEvent>);
      } else {
        await updateEra(editingItem.item.id, data as Partial<TimelineEra>);
      }
    },
    [editingItem, updateEvent, updateEra]
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-muted text-xs">Loading timeline...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-sm mb-4">{error}</p>
          <button
            onClick={onBack}
            className="text-primary-400 hover:text-primary-300 text-sm"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-border bg-surface">
        <div className="flex items-center justify-between h-12 px-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-1.5 text-muted hover:text-foreground transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span className="text-xs">Back</span>
            </button>
            <div className="h-4 w-px bg-border" />
            <h1 className="text-foreground font-medium text-sm">Bell Labs Timeline</h1>
          </div>

          <div className="flex items-center gap-4">
            <TimelineSearch
              events={events}
              eras={eras}
              people={people}
              scenes={scenes}
              onSelect={handleSearchSelect}
            />
            <div className="h-4 w-px bg-border" />
            <div className="flex items-center gap-3 text-[11px] text-muted">
              <span>{events.length} events</span>
              <span className="text-border">•</span>
              <span>{eras.length} eras</span>
              <span className="text-border">•</span>
              <span>{people.length} people</span>
              <span className="text-border">•</span>
              <span>{scenes.length} scenes</span>
            </div>
            <div className="h-4 w-px bg-border" />
            <img src="/story-logo.svg" alt="Story" className="h-5 opacity-80" />
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - People */}
        <PeopleSidebar
          people={people}
          visiblePeople={visiblePeople}
          onToggle={handleTogglePerson}
          onSelectAll={handleSelectAllPeople}
          onSelectNone={handleSelectNoPeople}
        />

        {/* Timeline Canvas - now takes full remaining width */}
        <div className="flex-1 relative">
          <TimelineCanvas
            ref={canvasRef}
            events={events}
            eras={eras}
            people={people}
            scenes={scenes}
            visiblePeople={visiblePeople}
            selectedSceneId={scenePopup?.sceneId || null}
            onHover={handleHover}
            onClick={handleClick}
            onSceneClick={handleSceneClick}
            onContextMenu={handleContextMenu}
          />

          {/* Instructions overlay */}
          <div className="absolute bottom-3 left-3 text-[10px] text-muted-foreground bg-surface/90 px-2.5 py-1.5 rounded border border-border">
            Scroll to zoom • Drag to pan • Click to view • Right-click to add
          </div>
        </div>
      </div>

      {/* Hover Card */}
      {hoveredItem && !contentPopup && !scenePopup && (
        <HoverCard
          type={hoveredItem.type}
          item={hoveredItem.item}
          x={hoveredItem.x}
          y={hoveredItem.y}
          canEdit={false}
          onEdit={() => {}}
          onViewFull={() => {
            if (hoveredItem.type === "event") {
              setContentPopup({ type: "event", item: hoveredItem.item as TimelineEvent, x: hoveredItem.x, y: hoveredItem.y });
            } else if (hoveredItem.type === "era") {
              setContentPopup({ type: "era", item: hoveredItem.item as TimelineEra, x: hoveredItem.x, y: hoveredItem.y });
            } else if (hoveredItem.type === "person") {
              setContentPopup({ type: "person", item: hoveredItem.item as TimelinePerson, x: hoveredItem.x, y: hoveredItem.y });
            }
          }}
        />
      )}

      {/* Edit Modal */}
      {editingItem && (
        <EditModal
          type={editingItem.type}
          item={editingItem.item}
          onSave={handleSave}
          onClose={() => setEditingItem(null)}
        />
      )}

      {/* Scene Detail Popup */}
      {scenePopup && (
        <SceneDetailPopup
          sceneId={scenePopup.sceneId}
          x={scenePopup.x}
          y={scenePopup.y}
          canEdit={canEditScope("scenes")}
          canEditCharacters={canEditScope("characters")}
          canEditStoryboard={canEditScope("storyboard")}
          onClose={() => setScenePopup(null)}
          onSceneUpdated={refetch}
        />
      )}

      {/* Content Detail Popup (events/eras/people) */}
      {contentPopup && (
        <ContentDetailPopup
          content={contentPopup}
          x={contentPopup.x}
          y={contentPopup.y}
          canEdit={canEdit}
          onClose={() => setContentPopup(null)}
          onSave={handleContentSave}
        />
      )}

      {/* Context Menu (right-click) */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          items={[
            {
              label: "Add Scene",
              icon: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                </svg>
              ),
              onClick: handleCreateScene,
            },
            {
              label: "Add Event",
              icon: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              onClick: handleCreateEvent,
            },
          ]}
        />
      )}
    </div>
  );
}
